import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Eye, Users, TrendingUp, FileText, Globe, Link as LinkIcon } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { api, formatApiError } from "@/lib/api";

const RANGES = [
  { label: "7 Tage", value: 7 },
  { label: "30 Tage", value: 30 },
  { label: "90 Tage", value: 90 },
];

const LANG_LABEL = { de: "Deutsch", en: "English", fr: "Français" };

function KpiCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border hairline p-6 bg-[var(--bg-1)]/40" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--text-2)]">{label}</span>
        <Icon size={16} className="text-[var(--gold)]" />
      </div>
      <div className="font-display text-4xl text-[var(--text-0)] leading-none">{value}</div>
      {sub && <div className="text-xs text-[var(--text-3)] mt-2">{sub}</div>}
    </div>
  );
}

function formatDay(iso) {
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
  } catch (_) {
    return iso;
  }
}

export default function AdminStats() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    api
      .get(`/stats/overview`, { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [days]);

  const chartData = useMemo(() => {
    if (!data?.daily?.length) return [];
    return data.daily.map((d) => ({ ...d, label: formatDay(d.day) }));
  }, [data]);

  const totalReferrerViews = (data?.referrers || []).reduce((s, r) => s + r.views, 0);

  return (
    <div data-testid="admin-stats" className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 pb-24">
      <Helmet>
        <title>Statistiken | Admin · Karim Benna IT Consulting</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-mono text-[var(--text-2)] hover:text-[var(--gold)] mb-4"
            data-testid="stats-back-dashboard"
          >
            <ArrowLeft size={14} /> Dashboard
          </Link>
          <h1 className="font-display text-3xl lg:text-4xl tracking-tight">Statistiken</h1>
          <p className="text-sm text-[var(--text-2)] mt-2 max-w-xl">
            Cookie-freies Tracking · IP täglich neu gehasht · Admin-Traffic wird ignoriert · Bots ausgefiltert.
          </p>
        </div>
        <div className="flex gap-2" role="tablist" aria-label="Zeitraum">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              data-testid={`stats-range-${r.value}`}
              className={`text-xs px-4 py-2 border transition-colors ${
                days === r.value
                  ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold-glow)]"
                  : "hairline text-[var(--text-2)] hover:text-[var(--text-0)]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div data-testid="stats-error" className="border border-red-500/40 text-red-500 p-3 mb-6 text-sm">
          {error}
        </div>
      )}

      {loading && (
        <div data-testid="stats-loading" className="text-center text-[var(--text-2)] py-24 font-mono text-xs uppercase tracking-[0.3em]">
          Laden…
        </div>
      )}

      {!loading && data && (
        <div className="space-y-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={Eye} label="Seitenaufrufe" value={data.totals.pageviews.toLocaleString("de-DE")} sub={`Letzte ${days} Tage`} />
            <KpiCard icon={Users} label="Besucher" value={data.totals.unique_visitors.toLocaleString("de-DE")} sub="Tägl. gehashter Fingerabdruck" />
            <KpiCard
              icon={TrendingUp}
              label="Top-Seite"
              value={data.top_paths?.[0]?.views?.toLocaleString("de-DE") || "0"}
              sub={data.top_paths?.[0]?.path || "—"}
            />
            <KpiCard
              icon={FileText}
              label="Top-Artikel"
              value={data.top_posts?.[0]?.views?.toLocaleString("de-DE") || "0"}
              sub={data.top_posts?.[0]?.slug || "—"}
            />
          </div>

          <section className="border hairline p-6 bg-[var(--bg-1)]/30">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl">Verlauf</h2>
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-3)]">
                Views &amp; Besucher pro Tag
              </span>
            </div>
            {chartData.length > 0 ? (
              <div style={{ width: "100%", height: 260 }} data-testid="stats-chart">
                <ResponsiveContainer>
                  <AreaChart data={chartData} margin={{ top: 8, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="statsViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--gold)" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="var(--gold)" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="statsVisitors" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--text-0)" stopOpacity={0.15} />
                        <stop offset="100%" stopColor="var(--text-0)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 6" stroke="var(--line)" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--text-2)" }} tickLine={false} axisLine={{ stroke: "var(--line)" }} />
                    <YAxis tick={{ fontSize: 11, fill: "var(--text-2)" }} tickLine={false} axisLine={{ stroke: "var(--line)" }} width={40} />
                    <Tooltip
                      contentStyle={{ background: "var(--bg-0)", border: "1px solid var(--line)", fontSize: 12 }}
                      labelStyle={{ color: "var(--text-1)" }}
                    />
                    <Area type="monotone" dataKey="views" stroke="var(--gold)" strokeWidth={2} fill="url(#statsViews)" name="Views" />
                    <Area type="monotone" dataKey="visitors" stroke="var(--text-0)" strokeWidth={1.5} fill="url(#statsVisitors)" name="Besucher" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center text-[var(--text-3)] text-sm py-16">
                Noch keine Aufrufe im gewählten Zeitraum. Sobald jemand die Seite besucht, erscheinen hier Daten.
              </div>
            )}
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <StatsTable
              testid="stats-top-paths"
              title="Top-Seiten"
              icon={TrendingUp}
              rows={data.top_paths}
              cols={[
                { key: "path", label: "Pfad", cls: "font-mono text-xs" },
                { key: "views", label: "Views", align: "right" },
                { key: "visitors", label: "Besucher", align: "right" },
              ]}
              empty="Keine Aufrufe im Zeitraum"
            />
            <StatsTable
              testid="stats-top-posts"
              title="Top-Blog-Artikel"
              icon={FileText}
              rows={data.top_posts}
              cols={[
                { key: "slug", label: "Slug", cls: "font-mono text-xs truncate max-w-[260px]" },
                { key: "views", label: "Views", align: "right" },
                { key: "visitors", label: "Besucher", align: "right" },
              ]}
              empty="Noch keine Blog-Aufrufe"
            />
            <StatsTable
              testid="stats-languages"
              title="Sprachverteilung"
              icon={Globe}
              rows={(data.languages || []).map((r) => ({
                ...r,
                lang: LANG_LABEL[r.lang] || r.lang || "unbekannt",
              }))}
              cols={[
                { key: "lang", label: "Sprache" },
                { key: "views", label: "Views", align: "right" },
              ]}
              empty="Keine Daten"
            />
            <StatsTable
              testid="stats-referrers"
              title="Top-Verweisquellen"
              icon={LinkIcon}
              rows={data.referrers}
              cols={[
                { key: "domain", label: "Domain", cls: "font-mono text-xs" },
                { key: "views", label: "Views", align: "right" },
              ]}
              empty={totalReferrerViews === 0 ? "Direktzugriffe — keine Referrer" : "Keine Daten"}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function StatsTable({ testid, title, icon: Icon, rows, cols, empty }) {
  return (
    <section className="border hairline bg-[var(--bg-1)]/30 p-6" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <Icon size={16} className="text-[var(--gold)]" />
        <h2 className="font-display text-lg">{title}</h2>
      </div>
      {rows && rows.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b hairline">
              {cols.map((c) => (
                <th
                  key={c.key}
                  className={`py-2 font-mono text-[10px] uppercase tracking-widest text-[var(--text-3)] ${
                    c.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className="border-b hairline last:border-b-0">
                {cols.map((c) => (
                  <td
                    key={c.key}
                    className={`py-2 ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.cls || ""}`}
                  >
                    {r[c.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-xs text-[var(--text-3)] py-8 text-center">{empty}</div>
      )}
    </section>
  );
}
